import { useState } from 'react';
import { APP_VERSION } from '../config.js';
import { weightFreshness } from '../rules/dosing.js';
import { DEFAULT_CONCENTRATIONS, concentrationsFor, hiddenDefaultsFor } from '../rules/concentrations.js';
import { notify } from '../lib/notify.js';
import { calculateAge, formatDaysAgo } from '../lib/format.js';
import { Calculator, Pencil, Plus, Save, Trash2, User, X } from '../components/icons.jsx';

export function SettingsView({darkMode, childrenList, customConcentrations, otherMedications, saveData, onShowChangelog, themePref, setThemePref, animationPref, setAnimationPref, onLogout}) {
  // A per-device nickname (e.g. "Mom's phone") stamped on doses this device logs,
  // so the shared History can show who logged what. Local only — not synced.
  const [deviceName, setDeviceNameState] = useState(() => {
    try { return localStorage.getItem('medDeviceName') || ''; } catch { return ''; }
  });
  const setDeviceName = (name) => {
    setDeviceNameState(name);
    try { localStorage.setItem('medDeviceName', name); } catch (e) {}
  };
  const [activeTab, setActiveTab] = useState('children');
  const [editingChild, setEditingChild] = useState(null);
  const [showChildForm, setShowChildForm] = useState(false); // the "+ Add child" form
  const [childForm, setChildForm] = useState({name: '', dob: '', weight: ''});
  const [concentrationMed, setConcentrationMed] = useState('paracetamol');
  const [newConcentration, setNewConcentration] = useState({mg: '', ml: ''});
  const [editingConcentration, setEditingConcentration] = useState(null);
  const [editConcentrationValue, setEditConcentrationValue] = useState({mg: '', ml: ''});
  const [newOtherMed, setNewOtherMed] = useState({ name: '', category: '' });
  const [editingOtherMed, setEditingOtherMed] = useState(null);
  const [editOtherMedValue, setEditOtherMedValue] = useState({ name: '', category: '' });
  const medicineCategories = [...new Set(otherMedications.map(m => m.category).filter(Boolean))].sort();

  const startEditOtherMed = (med) => {
    setEditingOtherMed(med.name);
    setEditOtherMedValue({ name: med.name, category: med.category || 'Other' });
  };
  const cancelEditOtherMed = () => {
    setEditingOtherMed(null);
    setEditOtherMedValue({ name: '', category: '' });
  };
  const saveEditOtherMed = () => {
    const newName = editOtherMedValue.name.trim();
    if (!newName) { notify('Enter a name'); return; }
    if (newName !== editingOtherMed && otherMedications.some(m => m.name === newName)) {
      notify('This name already exists');
      return;
    }
    saveData.otherMeds(otherMedications.map(m =>
      m.name === editingOtherMed ? { name: newName, category: editOtherMedValue.category.trim() || 'Other' } : m
    ));
    cancelEditOtherMed();
  };

  const saveChild = () => {
    if (!childForm.name || !childForm.dob || !childForm.weight) {
      notify('Please fill all fields');
      return;
    }
    // Same bounds as the Calculator's weight update. `w <= 0` alone let NaN
    // through, and every dose is computed from this number.
    const w = parseFloat(childForm.weight);
    if (!(w > 0 && w < 100)) {
      notify('Enter a weight in kg, e.g. 14.5');
      return;
    }
    if (!childForm.name.trim()) { notify('Please fill all fields'); return; }

    // weightUpdatedAt is what the Calculator's "still right?" check reads: a
    // new child or a changed weight stamps it now; renaming or fixing the date
    // of birth keeps the existing stamp (and any other fields) intact.
    const now = new Date().toISOString();
    if (editingChild) {
      saveData.children(childrenList.map(c =>
        c.id === editingChild
          ? { ...c, ...childForm, name: childForm.name.trim(), id: editingChild, weight: w,
              weightUpdatedAt: w !== parseFloat(c.weight) ? now : c.weightUpdatedAt }
          : c
      ));
    } else {
      saveData.children([...childrenList, {...childForm, name: childForm.name.trim(), id: crypto.randomUUID(), weight: w, weightUpdatedAt: now}]);
    }
    closeChildForm();
  };
  const closeChildForm = () => {
    setChildForm({name: '', dob: '', weight: ''});
    setEditingChild(null);
    setShowChildForm(false);
  };

  // One form for both adding and editing; the Children tab places it.
  const childInputCls = `w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
    darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const childFormEl = (
    <div className={`rounded-lg p-3 border space-y-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
        {editingChild ? 'Edit Child' : 'Add Child'}
      </h3>
      <input type="text" placeholder="Name" aria-label="Name" value={childForm.name}
        onChange={(e) => setChildForm({...childForm, name: e.target.value})}
        className={childInputCls} />
      <input type="date" aria-label="Date of birth" value={childForm.dob}
        onChange={(e) => setChildForm({...childForm, dob: e.target.value})}
        className={childInputCls} />
      <input type="number" inputMode="decimal" placeholder="Weight (kg)" aria-label="Weight (kg)" value={childForm.weight}
        onChange={(e) => setChildForm({...childForm, weight: e.target.value})}
        className={childInputCls} />
      <div className="flex gap-2">
        <button onClick={saveChild}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition flex items-center justify-center gap-2">
          <Save className="w-4 h-4 flex-shrink-0" />
          {editingChild ? 'Update' : 'Add'}
        </button>
        <button onClick={closeChildForm} aria-label="Cancel"
          className={`px-4 py-2 rounded-lg transition ${
            darkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
          }`}>
          <X className="w-4 h-4 flex-shrink-0" />
        </button>
      </div>
    </div>
  );

  // The list shown for the selected medicine: surviving built-ins first, then
  // added ones. Built-ins are tagged so it's clear what "Restore removed" will
  // bring back, and what a phone that hasn't updated yet will still be showing.
  const shownConcentrations = concentrationsFor(customConcentrations, concentrationMed);
  const hiddenDefaults = hiddenDefaultsFor(customConcentrations, concentrationMed);
  const isDefaultConc = (c) => (DEFAULT_CONCENTRATIONS[concentrationMed] || []).includes(c);

  // Every write goes through here so `hiddenDefaults` is always carried along —
  // dropping it would silently resurrect strengths the user removed.
  const saveConcentrations = ({ added, hidden }) => {
    saveData.concentrations({
      ...customConcentrations,
      [concentrationMed]: added !== undefined ? added : (customConcentrations[concentrationMed] || []),
      hiddenDefaults: {
        ...(customConcentrations.hiddenDefaults || {}),
        [concentrationMed]: hidden !== undefined ? hidden : hiddenDefaults,
      },
    });
  };

  // "300/5", from free-typed mg and ml. Returns null (after explaining) when the
  // numbers can't make a strength.
  const parseNewConcentration = ({ mg: mgRaw, ml: mlRaw }) => {
    const mg = parseFloat(mgRaw);
    const ml = parseFloat(mlRaw);
    if (!mg || !ml || mg <= 0 || ml <= 0) {
      notify('Invalid concentration values');
      return null;
    }
    return `${mg}/${ml}`;
  };

  const addConcentration = () => {
    const concString = parseNewConcentration(newConcentration);
    if (!concString) return;
    if (shownConcentrations.includes(concString)) {
      notify('This concentration already exists');
      return;
    }
    // Re-adding a removed built-in restores it in place rather than appending a
    // duplicate-looking copy at the bottom of the list.
    if (hiddenDefaults.includes(concString)) {
      saveConcentrations({ hidden: hiddenDefaults.filter(c => c !== concString) });
    } else {
      saveConcentrations({ added: [...(customConcentrations[concentrationMed] || []), concString] });
    }
    setNewConcentration({mg: '', ml: ''});
  };

  // Removing a built-in hides it; removing an added one drops it. Either way the
  // last surviving strength stays put: the Calculator has to have something to
  // dose against, and an empty dropdown is a worse answer than a stale entry.
  const removeConcentration = (conc) => {
    if (shownConcentrations.length === 1) {
      notify('Keep at least one concentration — the calculator needs one to work from.');
      return;
    }
    if (!confirm(`Remove ${conc.replace('/', ' mg / ')} ml?`)) return;
    if (isDefaultConc(conc)) saveConcentrations({ hidden: [...hiddenDefaults, conc] });
    else saveConcentrations({ added: (customConcentrations[concentrationMed] || []).filter(c => c !== conc) });
  };

  const startEditConcentration = (conc) => {
    const [mg, ml] = conc.split('/');
    setEditingConcentration(conc);
    setEditConcentrationValue({ mg, ml });
  };
  const cancelEditConcentration = () => {
    setEditingConcentration(null);
    setEditConcentrationValue({ mg: '', ml: '' });
  };
  const saveEditConcentration = () => {
    const next = parseNewConcentration(editConcentrationValue);
    if (!next) return;
    const original = editingConcentration;
    if (next === original) { cancelEditConcentration(); return; }
    if (shownConcentrations.includes(next)) {
      notify('This concentration already exists');
      return;
    }
    const added = customConcentrations[concentrationMed] || [];
    if (isDefaultConc(original)) {
      // A built-in can't be rewritten in place, so it's hidden and the new value
      // added — unless the new value is itself a hidden built-in, which is just
      // an unhide.
      const hidden = [...hiddenDefaults.filter(c => c !== next), original];
      saveConcentrations({ hidden, added: hiddenDefaults.includes(next) ? added : [...added, next] });
    } else {
      saveConcentrations({ added: added.map(c => (c === original ? next : c)) });
    }
    cancelEditConcentration();
  };

  return (
    <div className={`rounded-2xl shadow-xl p-4 sm:p-6 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
      <div className={`flex gap-2 mb-6 p-1 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
        <button onClick={() => setActiveTab('children')}
          className={`flex-1 min-w-0 py-2 px-2 sm:px-4 rounded-md font-medium text-sm sm:text-base transition-all flex items-center justify-center gap-1 sm:gap-2 ${
            activeTab === 'children' ? 'bg-indigo-600 text-white' : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
          }`}>
          <User className="hidden sm:block w-4 h-4 flex-shrink-0" />
          <span className="truncate">Children</span>
        </button>
        <button onClick={() => setActiveTab('concentrations')}
          className={`flex-1 min-w-0 py-2 px-2 sm:px-4 rounded-md font-medium text-sm sm:text-base transition-all flex items-center justify-center gap-1 sm:gap-2 ${
            activeTab === 'concentrations' ? 'bg-indigo-600 text-white' : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
          }`}>
          <Calculator className="hidden sm:block w-4 h-4 flex-shrink-0" />
          <span className="truncate">Strengths</span>
        </button>
        <button onClick={() => setActiveTab('other')}
          className={`flex-1 min-w-0 py-2 px-2 sm:px-4 rounded-md font-medium text-sm sm:text-base transition-all flex items-center justify-center gap-1 sm:gap-2 ${
            activeTab === 'other' ? 'bg-indigo-600 text-white' : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
          }`}>
          <Plus className="hidden sm:block w-4 h-4 flex-shrink-0" />
          <span className="truncate">Other meds</span>
        </button>
      </div>

      {activeTab === 'children' && (
        <div className="space-y-2">
          {/* Children first; the form only opens on "+ Add child" or ✎, and an
              edit opens in place of that child's card. */}
          {childrenList.length === 0 && !showChildForm && (
            <p className={`text-center py-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No children profiles yet
            </p>
          )}
          {childrenList.map(child => (
            editingChild === child.id ? (
              <div key={child.id}>{childFormEl}</div>
            ) : (
              <div key={child.id} className={`rounded-lg px-4 py-3 border flex justify-between items-center ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                <div>
                  <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {child.name}
                  </h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {calculateAge(child.dob)} • {child.weight} kg
                    {(() => {
                      const f = weightFreshness(child);
                      return (
                        <span className={f.stale ? (darkMode ? 'text-yellow-300' : 'text-yellow-700') : ''}>
                          {' • '}{f.known ? `weighed ${formatDaysAgo(f.days)}` : 'weight date unknown'}
                        </span>
                      );
                    })()}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => {
                    setChildForm({name: child.name, dob: child.dob, weight: child.weight});
                    setEditingChild(child.id);
                    setShowChildForm(false);
                  }}
                    aria-label={`Edit ${child.name}`}
                    className={`p-2 rounded-lg transition ${
                      darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                    }`}>
                    <Pencil className="w-4 h-4 flex-shrink-0" />
                  </button>
                  <button onClick={() => {
                    if (confirm(`Delete ${child.name}?`)) {
                      saveData.children(childrenList.filter(c => c.id !== child.id));
                    }
                  }}
                    aria-label={`Delete ${child.name}`}
                    className={`p-2 rounded-lg transition ${
                      darkMode ? 'hover:bg-gray-600 text-red-400' : 'hover:bg-gray-200 text-red-600'
                    }`}>
                    <Trash2 className="w-4 h-4 flex-shrink-0" />
                  </button>
                </div>
              </div>
            )
          ))}
          {showChildForm ? childFormEl : !editingChild && (
            <button onClick={() => { setChildForm({name: '', dob: '', weight: ''}); setShowChildForm(true); }}
              className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed font-medium transition ${
                darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>
              <Plus className="w-4 h-4 flex-shrink-0" /> Add child
            </button>
          )}
        </div>
      )}

      {activeTab === 'concentrations' && (
        <div>
          <div className={`flex rounded-lg p-1 mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <button onClick={() => { setConcentrationMed('paracetamol'); cancelEditConcentration(); }}
              className={`flex-1 min-w-0 truncate py-2 px-2 sm:px-4 rounded-md font-medium text-sm sm:text-base transition ${
                concentrationMed === 'paracetamol' ? 'bg-indigo-600 text-white' : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
              }`}>
              Paracetamol
            </button>
            <button onClick={() => { setConcentrationMed('ibuprofen'); cancelEditConcentration(); }}
              className={`flex-1 min-w-0 truncate py-2 px-2 sm:px-4 rounded-md font-medium text-sm sm:text-base transition ${
                concentrationMed === 'ibuprofen' ? 'bg-indigo-600 text-white' : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
              }`}>
              Ibuprofen
            </button>
          </div>

          <div className={`rounded-lg p-4 mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Add Strength (as on the bottle)
            </h3>
            <div className="flex gap-2 items-center">
              <input type="number" placeholder="mg" value={newConcentration.mg}
                onChange={(e) => setNewConcentration({...newConcentration, mg: e.target.value})}
                className={`flex-1 min-w-0 px-2 sm:px-4 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400'
                }`} />
              <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>/</span>
              <input type="number" placeholder="ml" value={newConcentration.ml}
                onChange={(e) => setNewConcentration({...newConcentration, ml: e.target.value})}
                className={`flex-1 min-w-0 px-2 sm:px-4 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400'
                }`} />
              <button onClick={addConcentration}
                className="flex-shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-lg transition">
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3 gap-2">
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Strengths
              </h3>
              {/* Only worth offering once something has actually been removed. */}
              {hiddenDefaults.length > 0 && (
                <button onClick={() => saveConcentrations({ hidden: [] })}
                  className={`text-xs font-medium px-2 py-1 rounded-lg transition ${
                    darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}>
                  Restore {hiddenDefaults.length} removed
                </button>
              )}
            </div>
            {shownConcentrations.length === 0 ? (
              <p className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No concentrations — add the one on your bottle above.
              </p>
            ) : (
              <div className="space-y-2">
                {shownConcentrations.map(conc => {
                  const [mg, ml] = conc.split('/');
                  return editingConcentration === conc ? (
                    <div key={conc} className={`rounded-lg p-3 border space-y-3 ${
                      darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex gap-2 items-center">
                        <input type="number" placeholder="mg" value={editConcentrationValue.mg}
                          onChange={(e) => setEditConcentrationValue({...editConcentrationValue, mg: e.target.value})}
                          className={`flex-1 min-w-0 px-2 sm:px-4 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                            darkMode ? 'bg-gray-600 border border-gray-500 text-white' : 'bg-white border border-gray-300 text-gray-900'
                          }`} />
                        <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>/</span>
                        <input type="number" placeholder="ml" value={editConcentrationValue.ml}
                          onChange={(e) => setEditConcentrationValue({...editConcentrationValue, ml: e.target.value})}
                          className={`flex-1 min-w-0 px-2 sm:px-4 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                            darkMode ? 'bg-gray-600 border border-gray-500 text-white' : 'bg-white border border-gray-300 text-gray-900'
                          }`} />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={saveEditConcentration}
                          className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition">
                          <Save className="w-4 h-4 flex-shrink-0" /> Save
                        </button>
                        <button onClick={cancelEditConcentration}
                          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium transition ${
                            darkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}>
                          <X className="w-4 h-4 flex-shrink-0" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div key={conc} className={`rounded-lg p-3 border flex justify-between items-center gap-2 ${
                      darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <span className={`font-medium min-w-0 truncate ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        {mg} mg / {ml} ml
                        {/* Says which entries "Restore removed" can bring back —
                            an edited or added one is gone for good once removed. */}
                        {isDefaultConc(conc) && (
                          <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            built-in
                          </span>
                        )}
                      </span>
                      <div className="flex items-center flex-shrink-0">
                        <button onClick={() => startEditConcentration(conc)}
                          className={`p-2 rounded-lg transition ${
                            darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                          }`}>
                          <Pencil className="w-4 h-4 flex-shrink-0" />
                        </button>
                        <button onClick={() => removeConcentration(conc)}
                          className={`p-2 rounded-lg transition ${
                            darkMode ? 'hover:bg-gray-600 text-red-400' : 'hover:bg-gray-200 text-red-600'
                          }`}>
                          <Trash2 className="w-4 h-4 flex-shrink-0" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'other' && (
        <div>
          <datalist id="medCategoryOptions">
            <option value="Painkillers" />
            {medicineCategories.map(c => (<option key={c} value={c} />))}
          </datalist>

          <div className={`rounded-lg p-4 mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Add Medication Name
            </h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <input type="text" placeholder="Name, e.g., Antihistamine" value={newOtherMed.name}
                onChange={(e) => setNewOtherMed({ ...newOtherMed, name: e.target.value })}
                className={`flex-1 min-w-0 px-2 sm:px-4 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400'
                }`} />
              <input type="text" list="medCategoryOptions" placeholder="Category, e.g., Antihistamines" value={newOtherMed.category}
                onChange={(e) => setNewOtherMed({ ...newOtherMed, category: e.target.value })}
                className={`flex-1 min-w-0 px-2 sm:px-4 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400'
                }`} />
              <button onClick={() => {
                const name = newOtherMed.name.trim();
                if (name && !otherMedications.some(m => m.name === name)) {
                  saveData.otherMeds([...otherMedications, { name, category: newOtherMed.category.trim() || 'Other' }]);
                  setNewOtherMed({ name: '', category: '' });
                } else if (otherMedications.some(m => m.name === name)) {
                  notify('This name already exists');
                }
              }}
                className="flex-shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition flex items-center justify-center">
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Category groups medicines together in History's filter (e.g. "Antibiotics"). Left blank, it's filed under "Other".
            </p>
          </div>

          <div>
            <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Medication Names
            </h3>
            <div className="space-y-2">
              {[...otherMedications].sort((a, b) => a.name.localeCompare(b.name)).map(med => (
                editingOtherMed === med.name ? (
                  <div key={med.name} className={`rounded-lg p-3 border flex flex-col sm:flex-row gap-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <input type="text" value={editOtherMedValue.name} autoFocus placeholder="Name"
                      onChange={(e) => setEditOtherMedValue({ ...editOtherMedValue, name: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEditOtherMed(); if (e.key === 'Escape') cancelEditOtherMed(); }}
                      className={`flex-1 min-w-0 px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                        darkMode ? 'bg-gray-600 border border-gray-500 text-white' : 'bg-white border border-gray-300 text-gray-900'
                      }`} />
                    <input type="text" list="medCategoryOptions" value={editOtherMedValue.category} placeholder="Category"
                      onChange={(e) => setEditOtherMedValue({ ...editOtherMedValue, category: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEditOtherMed(); if (e.key === 'Escape') cancelEditOtherMed(); }}
                      className={`flex-1 min-w-0 px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                        darkMode ? 'bg-gray-600 border border-gray-500 text-white' : 'bg-white border border-gray-300 text-gray-900'
                      }`} />
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={saveEditOtherMed}
                        className="flex-1 sm:flex-initial bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg transition text-sm font-medium">
                        Save
                      </button>
                      <button onClick={cancelEditOtherMed}
                        className={`flex-1 sm:flex-initial px-3 py-2 rounded-lg transition text-sm font-medium ${
                          darkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div key={med.name} className={`rounded-lg p-3 border flex justify-between items-center ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div>
                      <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        {med.name}
                      </span>
                      <span className={`block text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {med.category || 'Other'}
                      </span>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => startEditOtherMed(med)}
                        className={`p-2 rounded-lg transition ${
                          darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                        }`}>
                        <Pencil className="w-4 h-4 flex-shrink-0" />
                      </button>
                      <button onClick={() => {
                        if (otherMedications.length === 1) {
                          notify('Cannot delete the last medication name');
                          return;
                        }
                        saveData.otherMeds(otherMedications.filter(m => m.name !== med.name));
                      }}
                        className={`p-2 rounded-lg transition ${
                          darkMode ? 'hover:bg-gray-600 text-red-400' : 'hover:bg-gray-200 text-red-600'
                        }`}>
                        <Trash2 className="w-4 h-4 flex-shrink-0" />
                      </button>
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Per-phone settings, set once and rarely touched again, so they sit
          below the shared data rather than at the top. */}
      <div className={`mt-6 pt-4 border-t space-y-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>This phone</h3>
        <div>
          <label htmlFor="deviceName" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Name
          </label>
          <input id="deviceName" type="text" value={deviceName} onChange={(e) => setDeviceName(e.target.value)}
            placeholder="e.g., Mom's phone"
            className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
              darkMode ? 'bg-gray-700 border border-gray-600 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400'
            }`} />
          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Shown on doses this phone logs, so Records shows who logged what.
          </p>
        </div>
        <div>
          <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Appearance</p>
          <div role="radiogroup" aria-label="Appearance" className={`flex rounded-lg p-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            {[['system', 'Automatic'], ['light', 'Light'], ['dark', 'Dark']].map(([value, label]) => (
              <button key={value} role="radio" aria-checked={themePref === value} onClick={() => setThemePref(value)}
                className={`flex-1 py-1.5 rounded-md text-sm font-medium transition ${
                  themePref === value ? 'bg-indigo-600 text-white' : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
                }`}>
                {label}
              </button>
            ))}
          </div>
          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Automatic follows the phone's own light/dark setting.
          </p>
        </div>
        <div>
          <label htmlFor="animation" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Animation
          </label>
          <select id="animation" value={animationPref} onChange={(e) => setAnimationPref(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
              darkMode ? 'bg-gray-700 border border-gray-600 text-white' : 'bg-white border border-gray-300 text-gray-900'
            }`}>
            <option value="rise">Fade and rise</option>
            <option value="fade">Fade</option>
            <option value="none">None</option>
          </select>
          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            How the screen changes when you switch tabs.
          </p>
        </div>
        {onLogout && (
          <button onClick={onLogout}
            className={`w-full py-2 rounded-lg font-medium transition ${
              darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            Sign out
          </button>
        )}
      </div>

      <button onClick={onShowChangelog}
        className={`w-full mt-6 pt-4 border-t text-sm font-medium text-center transition ${
          darkMode ? 'border-gray-700 text-gray-400 hover:text-gray-200' : 'border-gray-200 text-gray-500 hover:text-gray-700'
        }`}>
        What's New (v{APP_VERSION})
      </button>
    </div>
  );
}
